import { useId, useState, type FormEvent } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksDialog } from '../../components/liks/LiksDialog/LiksDialog';
import { useI18n } from '../../i18n/useI18n';
import '../knowledge/CreateKnowledgeDatabaseDialog.css';
import './CreateTrainingExampleDialog.css';

function formatSortOrderForInput(value: number | null | undefined): string {
  return value == null ? '' : String(value);
}

function parseSortOrderInput(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    return undefined;
  }

  return parsed;
}

export type CreateTrainingExampleDialogProps = {
  title: string;
  questionLabel: string;
  questionPlaceholder: string;
  answerLabel: string;
  answerPlaceholder: string;
  sortOrderLabel: string;
  sortOrderPlaceholder: string;
  sortOrderHint: string;
  createLabel: string;
  cancelLabel: string;
  closeLabel: string;
  initialQuestion?: string;
  initialAnswer?: string;
  initialSortOrder?: number | null;
  isSubmitting?: boolean;
  readOnly?: boolean;
  showHowItWorks?: boolean;
  onCreate?: (input: {
    question: string;
    answer: string;
    sortOrder: number | null;
  }) => void;
  onClose: () => void;
};

export function CreateTrainingExampleDialog({
  title,
  questionLabel,
  questionPlaceholder,
  answerLabel,
  answerPlaceholder,
  sortOrderLabel,
  sortOrderPlaceholder,
  sortOrderHint,
  createLabel,
  cancelLabel,
  closeLabel,
  initialQuestion = '',
  initialAnswer = '',
  initialSortOrder = null,
  isSubmitting = false,
  readOnly = false,
  showHowItWorks = true,
  onCreate,
  onClose,
}: CreateTrainingExampleDialogProps) {
  const { translate } = useI18n();
  const questionFieldId = useId();
  const answerFieldId = useId();
  const sortOrderFieldId = useId();
  const sortOrderHintId = useId();
  const [question, setQuestion] = useState(initialQuestion);
  const [answer, setAnswer] = useState(initialAnswer);
  const [sortOrderInput, setSortOrderInput] = useState(() =>
    formatSortOrderForInput(initialSortOrder),
  );

  const parsedSortOrder = parseSortOrderInput(sortOrderInput);
  const isSortOrderInvalid =
    sortOrderInput.trim() !== '' && parsedSortOrder === undefined;
  const isCreateDisabled =
    readOnly ||
    isSubmitting ||
    question.trim() === '' ||
    answer.trim() === '' ||
    isSortOrderInvalid;
  const fieldsDisabled = isSubmitting || readOnly;

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isCreateDisabled || parsedSortOrder === undefined || onCreate == null) {
      return;
    }

    onCreate({
      question: question.trim(),
      answer: answer.trim(),
      sortOrder: parsedSortOrder,
    });
  };

  return (
    <LiksDialog
      isOpen
      as="form"
      size="md"
      title={title}
      onClose={onClose}
      onSubmit={handleSubmit}
      closeLabel={closeLabel}
      closeDisabled={isSubmitting}
      isDismissDisabled={isSubmitting}
      footer={
        <>
          <LiksButton
            type="button"
            variant="secondary"
            onClick={onClose}
            disabled={isSubmitting}
          >
            {readOnly ? closeLabel : cancelLabel}
          </LiksButton>
          {!readOnly && (
            <LiksButton type="submit" disabled={isCreateDisabled}>
              {createLabel}
            </LiksButton>
          )}
        </>
      }
    >
      {showHowItWorks && (
        <div
          className="create-training-dialog__notice"
          role="note"
          aria-label={translate('chatbots.training.createHowItWorksLabel')}
        >
          <p className="create-training-dialog__notice-title">
            {translate('chatbots.training.createHowItWorksTitle')}
          </p>
          <ul className="create-training-dialog__notice-list">
            <li>{translate('chatbots.training.createHowItWorksStructure')}</li>
            <li>{translate('chatbots.training.createHowItWorksClarity')}</li>
            <li>
              {translate('chatbots.training.createHowItWorksMultiMessage')}
            </li>
          </ul>
        </div>
      )}

      <label className="liks-knowledge-create__field" htmlFor={questionFieldId}>
        <span className="liks-knowledge-create__label">{questionLabel}</span>
        <textarea
          id={questionFieldId}
          className="liks-knowledge-create__textarea"
          value={question}
          onChange={(event) => setQuestion(event.target.value)}
          placeholder={questionPlaceholder}
          rows={3}
          disabled={fieldsDisabled}
          readOnly={readOnly}
        />
      </label>

      <label className="liks-knowledge-create__field" htmlFor={answerFieldId}>
        <span className="liks-knowledge-create__label">{answerLabel}</span>
        <textarea
          id={answerFieldId}
          className="liks-knowledge-create__textarea"
          value={answer}
          onChange={(event) => setAnswer(event.target.value)}
          placeholder={answerPlaceholder}
          rows={5}
          disabled={fieldsDisabled}
          readOnly={readOnly}
        />
      </label>

      <label
        className="liks-knowledge-create__field"
        htmlFor={sortOrderFieldId}
      >
        <span className="liks-knowledge-create__label">{sortOrderLabel}</span>
        <input
          id={sortOrderFieldId}
          className="liks-knowledge-create__input"
          type="number"
          inputMode="numeric"
          step={1}
          value={sortOrderInput}
          onChange={(event) => setSortOrderInput(event.target.value)}
          placeholder={sortOrderPlaceholder}
          disabled={fieldsDisabled}
          readOnly={readOnly}
          aria-invalid={isSortOrderInvalid}
          aria-describedby={sortOrderHintId}
        />
        <span id={sortOrderHintId} className="liks-knowledge-create__hint">
          {sortOrderHint}
        </span>
      </label>
    </LiksDialog>
  );
}
